import { Event, SimplePool, finalizeEvent, verifyEvent } from 'nostr-tools';
import { getNostrPool } from '../lib/nostrPool';
import { RELAYS } from '../config/constants';
import { Message } from '../types/chat';
import { logger } from '../lib/logger';
import { nip04Service } from './nip04Service';
import { nip44Service } from './nip44Service';
import { LoginMethod } from '../contexts/AuthContext';

/**
 * ChatService - Unified NIP-04 DM Service
 * High reliability implementation for nostr-tools v2
 */
class ChatService {
    private pool: SimplePool;
    private subscriptions: Map<string, any[]> = new Map();
    private seenEventIds = new Set<string>();

    constructor() {
        this.pool = getNostrPool();
    }

    // ===== MESSAGE SENDING =====

    async sendMessage(
        recipientPubkey: string,
        content: string,
        method: LoginMethod,
        sk?: Uint8Array,
        senderPubkey?: string
    ): Promise<void> {
        if (!method) throw new Error('Oturum bilgisi eksik.');
        if (method === 'read_only' && !window.nostr) throw new Error('Read-only mode active. A Nostr extension is required to send messages.');

        const safeMethod = method as 'extension' | 'seed' | 'read_only';

        if (!senderPubkey && method === 'seed' && sk) {
            const { getPublicKey } = await import('nostr-tools');
            senderPubkey = getPublicKey(sk);
        }

        console.log(`📤 [ChatService] Sending via ${method}:`, {
            to: recipientPubkey.slice(0, 8),
            from: senderPubkey?.slice(0, 8),
            len: content.length
        });

        try {
            const encryptionMethod = nip04Service.getBestMethod(safeMethod, sk);

            // Try NIP-44 first if supported, fallback to NIP-04
            let encrypted: string;
            if (nip44Service.isSupported(encryptionMethod)) {
                try {
                    encrypted = await nip44Service.encrypt(
                        content,
                        recipientPubkey,
                        encryptionMethod,
                        sk
                    );
                    console.log('📦 [ChatService] Using NIP-44 encryption');
                } catch (error) {
                    console.warn('⚠️ NIP-44 failed, falling back to NIP-04');
                    encrypted = await nip04Service.encrypt(
                        content,
                        recipientPubkey,
                        encryptionMethod,
                        sk
                    );
                }
            } else {
                encrypted = await nip04Service.encrypt(
                    content,
                    recipientPubkey,
                    encryptionMethod,
                    sk
                );
            }

            const eventTemplate = {
                kind: 4,
                content: encrypted,
                tags: [['p', recipientPubkey]],
                created_at: Math.floor(Date.now() / 1000),
            };

            let signedEvent: Event;

            if (method === 'extension') {
                if (!window.nostr) throw new Error('Extension not found');
                signedEvent = await window.nostr.signEvent(eventTemplate);
            } else if (sk) {
                signedEvent = finalizeEvent(eventTemplate, sk);
            } else {
                throw new Error('Signing credentials missing');
            }

            console.log(`📡 [ChatService] Publishing ${signedEvent.id.slice(0, 8)}...`);
            const pubs = this.pool.publish(RELAYS, signedEvent);

            try {
                // Wait for at least one relay to accept (v2 compatible)
                await Promise.race(pubs.map(p => new Promise((resolve) => {
                    p.then(() => resolve(true)).catch(() => { });
                })));
                console.log(`✅ [ChatService] At least one relay accepted the message.`);
            } catch (e) {
                // Continue anyway
            }

            logger.info('Message sent', { recipientPubkey });
        } catch (error) {
            console.error('❌ [ChatService] Send failed:', error);
            throw new Error('Mesaj gönderilemedi. Bağlantınızı kontrol edin.');
        }
    }

    // ===== TYPING INDICATORS =====

    async sendTyping(
        recipientPubkey: string,
        isTyping: boolean,
        method: LoginMethod,
        sk?: Uint8Array
    ): Promise<void> {
        if (!method || method === 'read_only') return;

        try {
            const eventTemplate = {
                kind: 20000,
                content: isTyping ? '1' : '0',
                tags: [['p', recipientPubkey]],
                created_at: Math.floor(Date.now() / 1000),
            };

            let signedEvent: Event;
            if (method === 'extension') {
                if (!window.nostr) return;
                signedEvent = await window.nostr.signEvent(eventTemplate);
            } else if (sk) {
                signedEvent = finalizeEvent(eventTemplate, sk);
            } else {
                return;
            }

            Promise.allSettled(this.pool.publish(RELAYS, signedEvent));
        } catch (error) {
            // Ignore typing indicator errors
        }
    }

    // ===== MESSAGE FETCHING =====

    async fetchConversation(
        otherPubkey: string,
        myPubkey: string,
        method: LoginMethod,
        sk?: Uint8Array
    ): Promise<Message[]> {
        if (!method) return [];
        const safeMethod = method as 'extension' | 'seed' | 'read_only';
        const messages: Message[] = [];
        console.log(`📥 [ChatService] Fetching: me:${myPubkey.slice(0, 8)} with:${otherPubkey.slice(0, 8)}`);

        try {
            // querySync only takes ONE filter in some v2 environments, so we run two in parallel
            const filterToMe = { kinds: [4], '#p': [myPubkey], authors: [otherPubkey], limit: 100 };
            const filterByMe = { kinds: [4], '#p': [otherPubkey], authors: [myPubkey], limit: 100 };

            const [eventsToMe, eventsByMe] = await Promise.all([
                this.pool.querySync(RELAYS, filterToMe),
                this.pool.querySync(RELAYS, filterByMe)
            ]);

            const allEvents = [...eventsToMe, ...eventsByMe];
            console.log(`📦 [ChatService] History returned ${allEvents.length} events (raw)`);

            for (const event of allEvents) {
                // In fetchConversation, we DON'T skip seenEventIds because we want a complete snapshot
                if (!verifyEvent(event)) continue;

                try {
                    const pTag = event.tags.find(t => t[0] === 'p');

                    // Decrypt using the partner's pubkey
                    const partnerPubkey = event.pubkey === myPubkey ? (pTag?.[1] || otherPubkey) : event.pubkey;

                    const decryptionMethod = nip04Service.getBestMethod(safeMethod, sk);
                    
                    // Try NIP-44 first, fallback to NIP-04
                    let decrypted: string;
                    if (nip44Service.isSupported(decryptionMethod)) {
                        try {
                            decrypted = await nip44Service.decrypt(
                                event.content,
                                partnerPubkey,
                                decryptionMethod,
                                sk
                            );
                        } catch {
                            decrypted = await nip04Service.decrypt(
                                event.content,
                                partnerPubkey,
                                decryptionMethod,
                                sk
                            );
                        }
                    } else {
                        decrypted = await nip04Service.decrypt(
                            event.content,
                            partnerPubkey,
                            decryptionMethod,
                            sk
                        );
                    }

                    messages.push({
                        id: event.id,
                        sender: event.pubkey,
                        recipient: pTag?.[1] || myPubkey,
                        content: decrypted,
                        timestamp: event.created_at,
                        sent: event.pubkey === myPubkey
                    });

                    // Still mark as seen just in case real-time picks it up later
                    this.seenEventIds.add(event.id);
                } catch (decryptError) {
                    continue;
                }
            }

            return messages.sort((a, b) => a.timestamp - b.timestamp);
        } catch (error) {
            console.error('❌ [ChatService] Fetch failed:', error);
            return [];
        }
    }

    // ===== REAL-TIME =====

    subscribeToMessages(
        myPubkey: string,
        method: LoginMethod,
        sk: Uint8Array | null,
        callback: (message: any) => void
    ): () => void {
        if (!method) return () => { };
        const safeMethod = method as 'extension' | 'seed' | 'read_only';
        const subId = `dm-${myPubkey}`;
        console.log(`🔔 [ChatService] Subscribing: ${myPubkey.slice(0, 8)}...`);

        // Close old multi-subs if they exist
        if (this.subscriptions.has(subId)) {
            this.subscriptions.get(subId)?.forEach(s => s.close());
        }

        const since = Math.floor(Date.now() / 1000) - 60; // Just 1 minute lookback for real-time

        // onevent handler shared between subs
        const handleEvent = async (event: Event) => {
            if (this.seenEventIds.has(event.id)) return;
            if (!verifyEvent(event)) return;

            try {
                if (event.kind === 20000) {
                    const pTag = event.tags.find(t => t[0] === 'p');
                    if (pTag?.[1] === myPubkey) {
                        callback({
                            type: 'typing',
                            pubkey: event.pubkey,
                            isTyping: event.content === '1'
                        });
                    }
                    return;
                }

                const pTag = event.tags.find(t => t[0] === 'p');
                const pubkeyForDecryption = event.pubkey === myPubkey
                    ? (pTag?.[1] || myPubkey)
                    : event.pubkey;

                const decryptionMethod = nip04Service.getBestMethod(safeMethod, sk || undefined);
                
                // Try NIP-44 first, fallback to NIP-04
                let decrypted: string;
                if (nip44Service.isSupported(decryptionMethod)) {
                    try {
                        decrypted = await nip44Service.decrypt(
                            event.content,
                            pubkeyForDecryption,
                            decryptionMethod,
                            sk || undefined
                        );
                    } catch {
                        decrypted = await nip04Service.decrypt(
                            event.content,
                            pubkeyForDecryption,
                            decryptionMethod,
                            sk || undefined
                        );
                    }
                } else {
                    decrypted = await nip04Service.decrypt(
                        event.content,
                        pubkeyForDecryption,
                        decryptionMethod,
                        sk || undefined
                    );
                }

                this.seenEventIds.add(event.id);

                callback({
                    id: event.id,
                    sender: event.pubkey,
                    recipient: pTag?.[1] || myPubkey,
                    content: decrypted,
                    timestamp: event.created_at
                });
            } catch (error) {
                // Decryption fail (event for someone else on shared relay)
            }
        };

        // nostalgia for nostr-tools v1: subscribeMany with array might fail, so we open two subs
        const subToMe = this.pool.subscribeMany(
            RELAYS,
            { kinds: [4, 20000], '#p': [myPubkey], since },
            { onevent: handleEvent }
        );

        const subByMe = this.pool.subscribeMany(
            RELAYS,
            { kinds: [4], authors: [myPubkey], since },
            { onevent: handleEvent }
        );

        this.subscriptions.set(subId, [subToMe, subByMe]);

        return () => {
            this.subscriptions.get(subId)?.forEach(s => s.close());
            this.subscriptions.delete(subId);
        };
    }

    // ===== PARTNERS =====

    async getConversationPartners(myPubkey: string): Promise<{ pubkey: string, lastMessage: string, timestamp: number }[]> {
        try {
            console.log('🔍 [ChatService] Listing partners with last messages...');

            const [eventsToMe, eventsByMe] = await Promise.all([
                this.pool.querySync(RELAYS, { kinds: [4], '#p': [myPubkey], limit: 100 }),
                this.pool.querySync(RELAYS, { kinds: [4], authors: [myPubkey], limit: 100 })
            ]);

            const allEvents = [...eventsToMe, ...eventsByMe].sort((a, b) => b.created_at - a.created_at);

            const partnersMap = new Map<string, { lastMessage: string, timestamp: number }>();

            for (const event of allEvents) {
                const partnerPubkey = event.pubkey === myPubkey
                    ? event.tags.find(t => t[0] === 'p')?.[1]
                    : event.pubkey;

                if (partnerPubkey && !partnersMap.has(partnerPubkey)) {
                    // Note: Content is still encrypted here, but we'll show a placeholder 
                    // or try to decrypt if possible. For now, we'll return the encrypted string
                    // or a 'Mesaj' indicator.
                    partnersMap.set(partnerPubkey, {
                        lastMessage: 'Korumalı Mesaj',
                        timestamp: event.created_at * 1000
                    });
                }
            }

            return Array.from(partnersMap.entries()).map(([pubkey, data]) => ({
                pubkey,
                ...data
            }));
        } catch (error) {
            console.error('Failed listing partners:', error);
            return [];
        }
    }

    async sendPresence(recipientPubkey: string, method: string, sk?: Uint8Array): Promise<void> {
        if (!method) return;
        try {
            const event: any = {
                kind: 20000,
                created_at: Math.floor(Date.now() / 1000),
                tags: [['p', recipientPubkey]],
                content: JSON.stringify({ type: 'presence' }),
            };

            const signedEvent = await nip04Service.signEvent(event, method as any, sk);
            Promise.allSettled(this.pool.publish(RELAYS, signedEvent));
        } catch (error) {
            // Ignore heartbeat errors
        }
    }
}

export const chatService = new ChatService();
